import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import {
  selectCompany,
  addCompany,
  setCompanies as setCompaniesAction,
} from "../../redux/slices/companySlice";
import { motion } from "framer-motion";
import { FaBuilding, FaSearch, FaPlus } from "react-icons/fa";
import CompanyCard from "./components/CompanyCard";
import AddCompanyModal from "./components/AddCompanyModal";
import { localStorageKey, screenPath } from "../../constant/strings";
import "./SelectCompany.css";
import { jwtDecode } from "jwt-decode";
import { getUserBusiness, base_url } from "../../services/api";
import axios from "axios";
import {
  setToken,
  setUserId,
  setBusinessData,
} from "../../redux/slices/userSlice";
import { createBusinessApi } from "../../services/apiCall";
import ToastMessage, { showToast } from "../../components/ToastMessage";

export default function SelectCompany() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userIds, setUserIds] = useState("");
  const userType = localStorage.getItem(localStorageKey.userType);

  const [page, setPage] = useState(1);
  const ITEMS_PER_PAGE = 12;

  const filteredCompanies = companies.filter(
    (c) =>
      c.name?.toLowerCase().includes(search.toLowerCase()) ||
      (c.email && c.email.toLowerCase().includes(search.toLowerCase())),
  );

  const displayedCompanies = filteredCompanies.slice(0, page * ITEMS_PER_PAGE);
  const hasMore = filteredCompanies.length > displayedCompanies.length;

  const handleLoadMore = () => {
    setPage((prev) => prev + 1);
  };
  const lastElementRef = useInfiniteScroll(false, hasMore, handleLoadMore);

  useEffect(() => {
    setPage(1);
  }, [search]);

  const initializeDashboard = async () => {
    try {
      const token = localStorage.getItem(localStorageKey.authToken);
      if (!token) return;

      const decoded = jwtDecode(token);
      const userId = decoded?.id;

      if (!userId) {
        console.warn("No user ID found in token");
        return;
      }
      dispatch(setToken(token));
      dispatch(setUserId(userId));
      setUserIds(userId);
      const url = `${base_url}${getUserBusiness}/${userId}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const data = response.data;
      let businessList = [];
      if (Array.isArray(data)) {
        businessList = data;
      } else if (data?.business && Array.isArray(data.business)) {
        businessList = data.business;
      } else if (data && typeof data === "object") {
        businessList = [data];
      }

      if (businessList.length > 0) {
        console.log("businessList....", businessList);
        setCompanies(businessList);
        dispatch(setCompaniesAction(businessList));
      } else {
        console.log("No businesses found");
      }
    } catch (err) {
      console.error("Error fetching businesses:", err);
    }
  };

  useEffect(() => {
    initializeDashboard();
  }, []);

  const handleAddCompany = (companyData) => {
    const company = {
      id: Date.now().toString(),
      name: companyData.name.trim(),
      owner: companyData.owner.trim(),
      email: companyData.email.trim(),
      phone: companyData.phone.trim(),
      logo: null,
    };

    dispatch(addCompany(company));
    setIsModalOpen(false);

    const payloadCreateBusiness = {
      email: companyData.email,
      name: companyData.owner,
      business_name: companyData.name,
      phone: companyData.phone,
      userId: userIds,
      postCode: "cv22 878",
      registrationNo: "1234567",
      vatNo: "1234567",
    };
    console.log("Payload:", payloadCreateBusiness);
    createBusinessApi(payloadCreateBusiness)
      .then((res) => {
        console.log("Company invited successfully:", res);
        if (res?.data?.status) {
          initializeDashboard();
          showToast("Company invited successfully", "success");
        }
      })
      .catch((err) => {
        console.error("Failed to invite company:", err);
      });
  };

  const handleSelect = (company) => {
    console.log(".......", company);
    dispatch(selectCompany(company));
    dispatch(setBusinessData(company));
    userType !== "company" && navigate(screenPath.dashboardPage);
    userType == "company" && navigate(screenPath.dashboardCompanyPage);
  };

  return (
    <>
      {/* MAIN PAGE */}
      <div className="select-company-wrapper">
        <ToastMessage />
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="select-company-container"
        >
          <div className="header-gradient">
            <h1>
              <FaBuilding style={{ marginRight: 12 }} />
              Select Your Company
            </h1>
            <p>Choose a company to continue to your dashboard</p>
          </div>

          {/* SEARCH BAR */}
          <div className="search-container">
            <FaSearch className="search-icon" color="#000" />
            <input
              type="text"
              placeholder="Search companies by name..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
          </div>

          {/* COMPANIES LIST */}
          <div className="company-grid">
            {displayedCompanies.map((company, index) => (
              <CompanyCard
                key={company.id}
                company={company}
                index={index}
                onSelect={handleSelect}
              />
            ))}

            {/* ADD NEW COMPANY CARD */}
            {userType !== "company" && (
              <motion.div
                whileHover={{ scale: 1.06 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setIsModalOpen(true)}
                className="company-card add-new-card"
              >
                <div className="plus-circle">
                  <FaPlus size={40} />
                </div>
                <h3>Add New Company</h3>
                <p>Create a new company profile</p>
              </motion.div>
            )}
          </div>

          {hasMore && (
            <div
              ref={lastElementRef}
              style={{
                textAlign: "center",
                marginTop: "20px",
                marginBottom: "40px",
              }}
            >
              <p style={{ color: "#888", fontSize: "0.9rem" }}>
                Loading more companies...
              </p>
            </div>
          )}

          {filteredCompanies.length === 0 && search && (
            <div className="empty-state">
              <FaBuilding size={70} opacity={0.15} />
              <p>No companies found matching "{search}"</p>
            </div>
          )}
        </motion.div>
      </div>

      {/* MODAL */}
      <AddCompanyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAddCompany}
      />
    </>
  );
}
